import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isCancel = location.pathname.includes("cancel");
  const sessionId = params.get("session_id");
  const [state, setState] = useState(isCancel ? "cancel" : "checking");
  const [plan, setPlan] = useState("");

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); setPlan(data.plan || ""); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (tries++ < 10) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId, isCancel]);

  const config = {
    checking: { icon: <Loader2 className="h-14 w-14 animate-spin text-[#FF5500]" />, title: "Validation du paiement…", sub: "Un instant, on confirme ta transaction." },
    success: { icon: <CheckCircle2 className="h-14 w-14 text-emerald-500" />, title: "Paiement confirmé 🎉", sub: `Ton plan ${plan} est actif. Bon mix !` },
    failed: { icon: <XCircle className="h-14 w-14 text-red-500" />, title: "Paiement non abouti", sub: "Aucun montant n'a été débité. Tu peux réessayer." },
    cancel: { icon: <XCircle className="h-14 w-14 text-slate-400" />, title: "Paiement annulé", sub: "Tu peux reprendre quand tu veux." },
    timeout: { icon: <Loader2 className="h-14 w-14 text-slate-400" />, title: "Vérification en cours", sub: "Le paiement peut prendre un moment. Vérifie ton historique plus tard." },
  }[state];

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full text-center bg-[#131722] border border-[#262C3A] rounded-3xl p-10 space-y-5" data-testid="payment-result">
        <div className="flex justify-center"><Logo className="h-9 w-9" /></div>
        <div className="flex justify-center">{config.icon}</div>
        <h1 className="font-heading text-2xl font-extrabold">{config.title}</h1>
        <p className="text-slate-400">{config.sub}</p>
        <div className="flex gap-3 pt-2">
          <button onClick={() => navigate("/studio")} data-testid="goto-studio-button" className="flex-1 bg-[#FF5500] hover:bg-[#E04800] rounded-xl py-3 font-semibold transition-colors">Ouvrir le Studio</button>
          <button onClick={() => navigate("/")} className="flex-1 border border-[#262C3A] hover:bg-[#1A1F2C] rounded-xl py-3 transition-colors">Accueil</button>
        </div>
      </motion.div>
    </div>
  );
}
